import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';
import { toast } from 'sonner';
import { Plus, CheckCircle2, Circle, Trash2, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const HomeworkPage = () => {
  const { getAuthHeader } = useAuth();
  const [homework, setHomework] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({
    subject_id: '',
    title: '',
    description: '',
    due_date: '',
    materials: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = getAuthHeader();
      const [hwRes, subRes] = await Promise.all([
        api.getHomework(headers),
        api.getSubjects(headers)
      ]);
      setHomework(hwRes.data);
      setSubjects(subRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Fehler beim Laden');
    }
  };

  const handleCreate = async () => {
    if (!formData.title.trim() || !formData.subject_id || !formData.due_date) {
      toast.error('Bitte fülle alle Pflichtfelder aus');
      return;
    }

    try {
      const headers = getAuthHeader();
      const data = {
        ...formData,
        due_date: new Date(formData.due_date).toISOString(),
        materials: formData.materials.split(',').map(m => m.trim()).filter(m => m)
      };
      await api.createHomework(data, headers);
      toast.success('Hausaufgabe hinzugefügt!');
      setShowDialog(false);
      setFormData({ subject_id: '', title: '', description: '', due_date: '', materials: '' });
      fetchData();
    } catch (error) {
      console.error('Error creating homework:', error);
      toast.error('Fehler beim Erstellen');
    }
  };

  const toggleComplete = async (id, currentStatus) => {
    try {
      const headers = getAuthHeader();
      await api.updateHomework(id, !currentStatus, headers);
      setHomework(prev => prev.map(hw => hw.id === id ? { ...hw, completed: !currentStatus } : hw));
      if (!currentStatus) toast.success('Hausaufgabe erledigt! +10 Punkte');
    } catch (error) {
      console.error('Error updating homework:', error);
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hausaufgabe löschen?')) return;
    try {
      const headers = getAuthHeader();
      await api.deleteHomework(id, headers);
      toast.success('Gelöscht');
      fetchData();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  const pendingHomework = homework.filter(hw => !hw.completed);
  const completedHomework = homework.filter(hw => hw.completed);

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-heading font-bold text-slate-900 mb-2">Hausaufgaben</h1>
          <p className="text-lg text-slate-600">{pendingHomework.length} offen, {completedHomework.length} erledigt</p>
        </div>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button data-testid="add-homework-button" className="rounded-full bg-primary-600 hover:bg-primary-700 text-white font-bold px-6">
              <Plus size={20} className="mr-2" />
              Hausaufgabe hinzufügen
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-3xl">
            <DialogHeader>
              <DialogTitle className="text-2xl font-heading font-bold">Neue Hausaufgabe</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Fach</label>
                <Select value={formData.subject_id} onValueChange={(value) => setFormData({ ...formData, subject_id: value })}>
                  <SelectTrigger data-testid="homework-subject-select" className="h-12 rounded-2xl">
                    <SelectValue placeholder="Fach wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {subjects.map(s => <SelectItem key={s.id} value={s.id}>{s.icon} {s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Titel</label>
                <Input
                  placeholder="z.B. Seite 42 Aufgaben 1-5"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  data-testid="homework-title-input"
                  className="h-12 rounded-2xl"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Beschreibung</label>
                <Input
                  placeholder="Details zur Hausaufgabe"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  data-testid="homework-description-input"
                  className="h-12 rounded-2xl"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Fälligkeitsdatum</label>
                <Input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  data-testid="homework-duedate-input"
                  className="h-12 rounded-2xl"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Materialien (durch Komma trennen)</label>
                <Input
                  placeholder="z.B. Heft, Taschenrechner"
                  value={formData.materials}
                  onChange={(e) => setFormData({ ...formData, materials: e.target.value })}
                  data-testid="homework-materials-input"
                  className="h-12 rounded-2xl"
                />
              </div>
              <Button onClick={handleCreate} data-testid="create-homework-button" className="w-full h-12 rounded-full bg-primary-600 hover:bg-primary-700 text-white font-bold">
                Hinzufügen
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-heading font-bold text-slate-900 mb-4">Offen</h2>
          {pendingHomework.length === 0 ? (
            <div className="bg-white rounded-3xl p-12 text-center border border-slate-100">
              <p className="text-slate-500">Keine offenen Hausaufgaben</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pendingHomework.map(hw => {
                const subject = subjects.find(s => s.id === hw.subject_id);
                const dueDate = new Date(hw.due_date);
                const isOverdue = dueDate < new Date();
                return (
                  <div key={hw.id} data-testid={`homework-${hw.id}`} className={`bg-white rounded-3xl p-6 border-2 ${isOverdue ? 'border-red-200' : 'border-slate-100'} hover:shadow-md transition-all duration-300`}>
                    <div className="flex items-start gap-4">
                      <button onClick={() => toggleComplete(hw.id, hw.completed)} data-testid={`homework-checkbox-${hw.id}`} className="mt-1">
                        <Circle className="text-slate-400 hover:text-primary-600 transition-colors" size={28} strokeWidth={2} />
                      </button>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {subject && <span className="text-xl">{subject.icon}</span>}
                          <span className="text-sm font-medium text-slate-600">{subject?.name || 'Unbekannt'}</span>
                        </div>
                        <h3 className="text-lg font-heading font-bold text-slate-900">{hw.title}</h3>
                        <p className="text-sm text-slate-600 mt-1">{hw.description}</p>
                        <div className="flex items-center gap-2 mt-3">
                          <Calendar size={14} className={isOverdue ? 'text-red-600' : 'text-slate-500'} />
                          <span className={`text-xs font-medium ${isOverdue ? 'text-red-600' : 'text-slate-600'}`}>
                            Fällig: {dueDate.toLocaleDateString('de-DE')} {isOverdue && '(Überfällig!)'}
                          </span>
                        </div>
                        {hw.materials && hw.materials.length > 0 && (
                          <div className="flex gap-2 mt-2">
                            {hw.materials.map((m, i) => (
                              <span key={i} className="px-2 py-1 bg-indigo-50 text-indigo-700 text-xs rounded-full">{m}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(hw.id)} data-testid={`delete-homework-${hw.id}`} className="text-red-600 hover:bg-red-50 rounded-xl">
                        <Trash2 size={20} />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div>
          <h2 className="text-2xl font-heading font-bold text-slate-900 mb-4">Erledigt</h2>
          {completedHomework.length === 0 ? (
            <div className="bg-white rounded-3xl p-12 text-center border border-slate-100">
              <p className="text-slate-500">Noch keine erledigten Hausaufgaben</p>
            </div>
          ) : (
            <div className="space-y-3">
              {completedHomework.map(hw => {
                const subject = subjects.find(s => s.id === hw.subject_id);
                return (
                  <div key={hw.id} className="bg-green-50 rounded-3xl p-6 border-2 border-green-200">
                    <div className="flex items-start gap-4">
                      <button onClick={() => toggleComplete(hw.id, hw.completed)} className="mt-1">
                        <CheckCircle2 className="text-green-600" size={28} strokeWidth={2.5} />
                      </button>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {subject && <span className="text-xl">{subject.icon}</span>}
                          <span className="text-sm font-medium text-green-700">{subject?.name || 'Unbekannt'}</span>
                        </div>
                        <h3 className="text-lg font-heading font-bold text-green-900 line-through">{hw.title}</h3>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HomeworkPage;
